import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { z } from 'zod';
import { authApi } from './api';
import { FlowBackground } from './FlowBackground';
import { AuthFooter } from './AuthFooter';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';

type ResetInput = {
  otp: string;
  newPassword: string;
  confirmPassword: string;
};

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const t = useT();
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';

  const schema = z
    .object({
      otp: z.string().length(6, t('auth.reset.errOtpLength')),
      newPassword: z.string().min(8, t('auth.reset.errPasswordShort')),
      confirmPassword: z.string(),
    })
    .refine((d) => d.newPassword === d.confirmPassword, {
      path: ['confirmPassword'],
      message: t('auth.reset.errMismatch'),
    });

  const form = useForm<ResetInput>({ resolver: zodResolver(schema) });

  const reset = useMutation({
    mutationFn: (v: ResetInput) =>
      authApi.resetPassword({
        email,
        otp: v.otp,
        newPassword: v.newPassword,
      }),
    onSuccess: () => {
      toast({
        tone: 'success',
        title: t('auth.reset.successTitle'),
        description: t('auth.reset.successBody'),
      });
      navigate('/login');
    },
    onError: (e: Error) =>
      toast({
        tone: 'error',
        title: t('auth.reset.failed'),
        description: e.message,
      }),
  });

  return (
    <div className="relative grid min-h-screen lg:grid-cols-2">
      <FlowBackground />
      <div className="relative z-10 flex items-center justify-center px-6 py-12">
        <AuthFooter />
        <div
          className="w-full max-w-md rounded-2xl border border-white/[0.06] bg-[rgb(10_6_19/0.55)] p-8 shadow-[0_30px_80px_-20px_rgb(0_0_0/0.7)] backdrop-blur-xl"
          style={{
            boxShadow:
              '0 30px 80px -20px rgb(0 0 0 / 0.7), 0 0 0 1px color-mix(in srgb, var(--color-xk-accent) 8%, transparent) inset',
          }}
        >
          <div className="mb-8 flex items-center gap-2">
            <img src="/xk-icon.svg" alt="Xamplin" className="h-9 w-9 rounded-lg" />
            <span className="font-display text-lg font-bold tracking-tight">xamplin</span>
          </div>

          <h1 className="text-3xl font-bold">{t('auth.reset.title')}</h1>
          <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
            {email
              ? t('auth.reset.subtitleFor', { email })
              : t('auth.reset.subtitleNoEmail')}
          </p>

          {!email ? (
            <div className="mt-8 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4 text-sm">
              <Link to="/auth/forgot-password" className="font-semibold text-[var(--color-accent)]">
                {t('auth.reset.requestNew')}
              </Link>
            </div>
          ) : (
            <form
              className="mt-8 space-y-4"
              onSubmit={form.handleSubmit((v) => reset.mutate(v))}
            >
              <FormField
                label={t('auth.reset.otpLabel')}
                error={form.formState.errors.otp?.message}
              >
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  placeholder="123456"
                  maxLength={6}
                  {...form.register('otp')}
                />
              </FormField>
              <FormField
                label={t('auth.reset.newPasswordLabel')}
                error={form.formState.errors.newPassword?.message}
              >
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
                  <Input
                    type="password"
                    autoComplete="new-password"
                    className="pl-9"
                    {...form.register('newPassword')}
                  />
                </div>
              </FormField>
              <FormField
                label={t('auth.reset.confirmPasswordLabel')}
                error={form.formState.errors.confirmPassword?.message}
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  {...form.register('confirmPassword')}
                />
              </FormField>
              <Button type="submit" loading={reset.isPending} className="w-full" size="lg">
                {t('auth.reset.submit')}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-[var(--color-fg-muted)]">
            <Link to="/login" className="font-semibold text-[var(--color-accent)] hover:underline">
              {t('auth.forgot.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
